import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const IndustriesNew = () => {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/industry-menu?active_only=true`);
        if (response.ok) {
          const data = await response.json();
          setIndustries(data);
        }
      } catch (error) {
        console.error('Failed to fetch industries:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f4f0]">
        <Loader2 className="w-10 h-10 animate-spin text-[#2a2a2a]" />
      </div>
    );
  }

  const totalSolutions = industries.reduce((acc, i) => acc + (i.sub_items?.length || 0), 0);
  const totalCaseStudies = industries.reduce((acc, i) => acc + (i.case_studies?.length || 0), 0);

  return (
    <div className="min-h-screen bg-[#f5f4f0]">
      <SEO 
        title="Industries We Serve | Navaris Digital"
        description="Specialized technology solutions for diverse industries"
      />
      
      {/* Hero */}
      <section className="pt-28 pb-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-6">
            <Link to="/" className="hover:text-[#2a2a2a] transition-colors">Home</Link>
            <ChevronRight size={14} />
            <span className="text-[#2a2a2a]">Industries</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-4xl md:text-5xl font-bold text-[#2a2a2a] mb-3"
              >
                Industries We Serve
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-gray-600 max-w-lg"
              >
                Specialized solutions tailored for your sector's unique challenges
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex items-center gap-6"
            >
              <div className="text-center">
                <div className="text-2xl font-bold text-[#2a2a2a]">{industries.length}</div>
                <div className="text-xs text-gray-500">Industries</div>
              </div>
              <div className="w-px h-8 bg-gray-300" />
              <div className="text-center">
                <div className="text-2xl font-bold text-[#2a2a2a]">{totalSolutions}</div>
                <div className="text-xs text-gray-500">Solutions</div>
              </div>
              <div className="w-px h-8 bg-gray-300" />
              <div className="text-center">
                <div className="text-2xl font-bold text-[#2a2a2a]">{totalCaseStudies}</div>
                <div className="text-xs text-gray-500">Case Studies</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Masonry Grid */}
      <section className="pb-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {industries.map((industry, index) => {
              const isLarge = index === 0;
              const isWide = index === 3;
              const sizeClass = isLarge ? 'col-span-2 row-span-2' : isWide ? 'col-span-2' : '';
              const heightClass = isLarge ? 'h-[340px]' : 'h-[165px]';
              
              return (
                <motion.div
                  key={industry.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className={sizeClass}
                >
                  <Link
                    to={`/industries/${industry.slug}`}
                    className={`group relative block rounded-xl overflow-hidden ${heightClass}`}
                  >
                    {/* Image */}
                    <img 
                      src={industry.hero_image || 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800'}
                      alt={industry.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2a2a2a]/90 via-[#2a2a2a]/30 to-transparent group-hover:from-[#2a2a2a]/95 group-hover:via-[#2a2a2a]/50 transition-all duration-300" />

                    {/* Content */}
                    <div className="absolute inset-0 p-3 flex flex-col justify-between">
                      {/* Top */}
                      <div className="flex items-start justify-between">
                        <span className="text-white/50 text-[10px] font-medium">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <div className="w-6 h-6 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <ArrowUpRight size={12} className="text-white" />
                        </div>
                      </div>

                      {/* Bottom */}
                      <div>
                        {/* Tags on hover */}
                        {industry.sub_items && industry.sub_items.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                            {industry.sub_items.slice(0, isLarge ? 3 : 2).map((sub) => (
                              <span 
                                key={sub.id}
                                className="px-1.5 py-0.5 bg-white/20 backdrop-blur-sm rounded text-[10px] text-white"
                              >
                                {sub.name}
                              </span>
                            ))}
                            {industry.sub_items.length > (isLarge ? 3 : 2) && (
                              <span className="text-[10px] text-white/60">
                                +{industry.sub_items.length - (isLarge ? 3 : 2)}
                              </span>
                            )}
                          </div>
                        )}

                        <h3 className={`font-bold text-white ${isLarge ? 'text-xl mb-1' : 'text-sm'}`}>
                          {industry.name}
                        </h3>
                        
                        {isLarge && (
                          <p className="text-white/70 text-xs line-clamp-2 mb-1">
                            {industry.description}
                          </p>
                        )}

                        <div className="flex items-center gap-2 text-[10px] text-white/50">
                          <span>{industry.sub_items?.length || 0} solutions</span>
                          {industry.case_studies?.length > 0 && (
                            <>
                              <span>•</span>
                              <span>{industry.case_studies.length} cases</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}

            {/* CTA Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="h-[165px] bg-[#2a2a2a] rounded-xl p-3 flex flex-col justify-between">
                <div>
                  <h3 className="font-semibold text-white text-sm mb-1">Need something else?</h3>
                  <p className="text-gray-400 text-xs">We work across all sectors</p>
                </div>
                <Button asChild size="sm" className="w-fit bg-white text-[#2a2a2a] hover:bg-gray-100 h-7 text-xs px-3">
                  <Link to="/contact">
                    Contact Us <ArrowRight className="ml-1" size={12} />
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      {totalCaseStudies > 0 && (
        <section className="py-12 bg-[#eae9e5]">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-[#2a2a2a]">Success Stories</h2>
                <p className="text-gray-500 text-sm">Proven results across industries</p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              {industries
                .filter(i => i.case_studies && i.case_studies.length > 0)
                .slice(0, 3)
                .map((industry) => {
                  const cs = industry.case_studies[0];
                  return (
                    <Link
                      key={cs.id || industry.id}
                      to={`/industries/${industry.slug}#case-studies`}
                      className="group relative rounded-xl overflow-hidden h-[140px]"
                    >
                      {cs.image && (
                        <img 
                          src={cs.image} 
                          alt={cs.title}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#2a2a2a]/95 via-[#2a2a2a]/50 to-transparent" />
                      
                      <div className="absolute inset-0 p-3 flex flex-col justify-end">
                        <span className="px-1.5 py-0.5 bg-white/20 backdrop-blur-sm rounded text-[10px] text-white w-fit mb-1">
                          {industry.name}
                        </span>
                        <h3 className="font-semibold text-white text-sm line-clamp-1">
                          {cs.title}
                        </h3>
                        <div className="flex items-center justify-between">
                          <p className="text-white/60 text-xs">{cs.client}</p>
                          {cs.results && (
                            <p className="text-green-400 text-xs font-medium">{cs.results}</p>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-12 bg-[#f5f4f0]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-xl font-bold text-[#2a2a2a] mb-2">Ready to Transform Your Business?</h2>
          <p className="text-gray-600 text-sm mb-4">Let's discuss your specific needs</p>
          <Button asChild className="bg-[#2a2a2a] hover:bg-[#3a3a3a]">
            <Link to="/contact">
              Get Started <ArrowRight className="ml-2" size={16} />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default IndustriesNew;
